import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  dayKey,
  msUntilNextLocalMidnight,
  rebaseState,
  safeTimeZone,
  type DayKey,
  type StateV2,
} from '@superposition/domain';
import * as api from './api.js';
import { ApiError } from './api.js';
import { getInitData, guessTimeZone, isTelegramEnvironment } from './telegram.js';
import { useT } from '../i18n/index.js';

/** явные состояния сохранения: «сохранено» показывается только после ответа сервера */
export type SaveStatus =
  | { kind: 'clean' }
  | { kind: 'pending' }
  | { kind: 'saving' }
  | { kind: 'saved'; at: number }
  | { kind: 'failed'; message: string; canRetry: boolean }
  | { kind: 'conflict'; message: string };

export type Phase =
  | { kind: 'loading' }
  | { kind: 'ready' }
  | { kind: 'no_telegram' }
  | { kind: 'no_access'; telegramId: string; expiredAt: string | null; renewUrl: string; buyUrl?: string | null }
  | { kind: 'auth_failed'; message: string };

interface StoreValue {
  phase: Phase;
  state: StateV2 | null;
  revision: number;
  save: SaveStatus;
  today: DayKey;
  timezone: string;
  /** локальное изменение + отложенное сохранение на сервер */
  update: (recipe: (draft: StateV2) => StateV2) => void;
  /** немедленно отправить накопленное */
  flush: () => Promise<void>;
  retry: () => void;
  /** перечитать состояние с сервера (после конфликта) */
  reload: () => Promise<void>;
  /** заменить состояние целиком после импорта */
  adopt: (revision: number, state: StateV2) => void;
  /** черновик для копирования и выгрузки, когда сервер недоступен */
  draftJson: () => string;
  signIn: () => Promise<void>;
}

const StoreContext = createContext<StoreValue | null>(null);

const SAVE_DEBOUNCE_MS = 700;

export function StoreProvider({ children }: { children: ReactNode }): ReactNode {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [state, setState] = useState<StateV2 | null>(null);
  const [revision, setRevision] = useState(0);
  const [save, setSave] = useState<SaveStatus>({ kind: 'clean' });
  const [timezone, setTimezone] = useState<string>(() => safeTimeZone(guessTimeZone()));
  const [today, setToday] = useState<DayKey>(() => dayKey(new Date(), safeTimeZone(guessTimeZone())));

  // последнее, что нужно отправить; ref, чтобы таймер видел свежие данные
  const pendingRef = useRef<StateV2 | null>(null);
  const revisionRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef(false);
  // версия, которую подтвердил сервер: от неё считается, что изменилось здесь
  const baseRef = useRef<StateV2 | null>(null);
  // 409 решается слиянием; если другое устройство пишет без остановки и слияние
  // не успевает, сохранение замирает и показывает черновик, как раньше
  const conflictRef = useRef(false);
  const rebaseStreakRef = useRef(0);
  // словарь через ref: колбэки сохранения создаются один раз и не пересоздаются при смене языка
  const t = useT();
  const tRef = useRef(t);
  tRef.current = t;

  revisionRef.current = revision;

  const pushNow = useCallback(async (): Promise<void> => {
    if (inFlightRef.current || conflictRef.current) return;
    const next = pendingRef.current;
    if (!next) return;

    inFlightRef.current = true;
    setSave({ kind: 'saving' });
    // повтор запускается после finally: иначе finally сбросит флаг уже идущего запроса
    let again = false;
    try {
      const saved = await api.saveState(revisionRef.current, next);
      // за время запроса могли добавиться новые правки — отправим их следующим заходом
      const stillPending = pendingRef.current;
      setRevision(saved.revision);
      revisionRef.current = saved.revision;
      baseRef.current = next;
      rebaseStreakRef.current = 0;
      if (stillPending && stillPending !== next) {
        again = true;
        return;
      }
      pendingRef.current = null;
      setSave({ kind: 'saved', at: Date.now() });
    } catch (err) {
      if (err instanceof ApiError && err.kind === 'no_access' && err.body?.access) {
        // черновик остаётся в pendingRef: после продления его можно отправить заново
        setPhase({ kind: 'no_access', ...err.body.access });
        setSave({ kind: 'failed', message: api.describeFailure(err, tRef.current), canRetry: false });
      } else if (
        err instanceof ApiError &&
        err.kind === 'conflict' &&
        err.body?.state &&
        typeof err.body.revision === 'number' &&
        baseRef.current &&
        rebaseStreakRef.current < 3
      ) {
        // другое устройство или бот успели сохранить: накладываем здешние правки
        // на свежую серверную версию и отправляем еще раз, без окон и черновиков
        rebaseStreakRef.current += 1;
        const server = err.body.state;
        const local = pendingRef.current ?? next;
        const merged = rebaseState(baseRef.current, local, server);
        baseRef.current = server;
        revisionRef.current = err.body.revision;
        setRevision(err.body.revision);
        pendingRef.current = merged;
        setState(merged);
        again = true;
        return;
      } else if (err instanceof ApiError && err.kind === 'conflict') {
        conflictRef.current = true;
        setSave({
          kind: 'conflict',
          message: tRef.current.save.conflict,
        });
      } else {
        setSave({
          kind: 'failed',
          message: api.describeFailure(err, tRef.current),
          canRetry: !(err instanceof ApiError && err.kind === 'unauthorized'),
        });
      }
    } finally {
      inFlightRef.current = false;
      if (again) void pushNow();
    }
  }, []);

  const scheduleSave = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (conflictRef.current) return;
    setSave({ kind: 'pending' });
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void pushNow();
    }, SAVE_DEBOUNCE_MS);
  }, [pushNow]);

  const update = useCallback(
    (recipe: (draft: StateV2) => StateV2) => {
      setState((prev) => {
        if (!prev) return prev;
        const next = recipe(prev);
        pendingRef.current = next;
        return next;
      });
      scheduleSave();
    },
    [scheduleSave],
  );

  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    await pushNow();
  }, [pushNow]);

  const retry = useCallback(() => {
    if (pendingRef.current) void pushNow();
  }, [pushNow]);

  const reload = useCallback(async () => {
    const env = await api.fetchState();
    setRevision(env.revision);
    revisionRef.current = env.revision;
    baseRef.current = env.state;
    setState(env.state);
    setTimezone(env.state.timezone);
    pendingRef.current = null;
    conflictRef.current = false;
    setSave({ kind: 'clean' });
  }, []);

  const adopt = useCallback((rev: number, next: StateV2) => {
    setRevision(rev);
    revisionRef.current = rev;
    baseRef.current = next;
    setState(next);
    pendingRef.current = null;
    conflictRef.current = false;
    setSave({ kind: 'saved', at: Date.now() });
  }, []);

  const draftJson = useCallback(() => {
    const draft = pendingRef.current ?? state;
    return draft ? JSON.stringify(draft, null, 2) : '{}';
  }, [state]);

  const signIn = useCallback(async () => {
    setPhase({ kind: 'loading' });
    const initData = getInitData();
    if (!initData) {
      setPhase({ kind: 'no_telegram' });
      return;
    }
    const tz = safeTimeZone(guessTimeZone());
    try {
      const auth = await api.authTelegram(initData, tz);
      const env = await api.fetchState();
      setRevision(env.revision);
      revisionRef.current = env.revision;
      baseRef.current = env.state;
      setState(env.state);
      setTimezone(env.state.timezone || auth.user.timezone);
      setToday(dayKey(new Date(), env.state.timezone || tz));
      setSave({ kind: 'clean' });
      setPhase({ kind: 'ready' });
    } catch (err) {
      if (err instanceof ApiError && err.kind === 'no_access' && err.body?.access) {
        setPhase({ kind: 'no_access', ...err.body.access });
        return;
      }
      setPhase({ kind: 'auth_failed', message: api.describeFailure(err, tRef.current) });
    }
  }, []);

  useEffect(() => {
    if (!isTelegramEnvironment()) {
      setPhase({ kind: 'no_telegram' });
      return;
    }
    void signIn();
  }, [signIn]);

  // пересчёт календарного дня при наступлении местной полуночи
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const schedule = (): void => {
      const ms = msUntilNextLocalMidnight(new Date(), timezone) + 1000;
      timer = setTimeout(() => {
        setToday(dayKey(new Date(), timezone));
        schedule();
      }, ms);
    };
    setToday(dayKey(new Date(), timezone));
    schedule();
    return () => clearTimeout(timer);
  }, [timezone]);

  // уход со страницы не должен оставлять несохранённое в подвешенном виде,
  // а возвращение — показывать устаревшее: за это время могли писать с другого
  // устройства или бот по рассказу о дне
  useEffect(() => {
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') {
        if (pendingRef.current) void pushNow();
        return;
      }
      if (!baseRef.current || conflictRef.current || inFlightRef.current) return;
      if (pendingRef.current) {
        void pushNow();
        return;
      }
      void api
        .fetchState()
        .then((env) => {
          // пока шел запрос, человек мог начать правку — ее не трогаем
          if (pendingRef.current || inFlightRef.current || env.revision === revisionRef.current) return;
          setRevision(env.revision);
          revisionRef.current = env.revision;
          baseRef.current = env.state;
          setState(env.state);
        })
        .catch(() => {
          // нет сети — покажем то, что есть; следующее сохранение сольется с сервером
        });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [pushNow]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const value = useMemo<StoreValue>(
    () => ({
      phase,
      state,
      revision,
      save,
      today,
      timezone,
      update,
      flush,
      retry,
      reload,
      adopt,
      draftJson,
      signIn,
    }),
    [phase, state, revision, save, today, timezone, update, flush, retry, reload, adopt, draftJson, signIn],
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore(): StoreValue {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore вызван вне StoreProvider');
  return ctx;
}

/** состояние гарантированно загружено: для экранов внутри phase === ready */
export function useLoadedState(): { state: StateV2; store: StoreValue } {
  const store = useStore();
  if (!store.state) throw new Error('состояние ещё не загружено');
  return { state: store.state, store };
}
